import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';
import type { LayerSettings } from '../LayerSettingsPanel';
import { prepareMapForLayerToggles, isMapStyleReady } from '../../../utils/mapLayerPromotion';
import { enableMapbox3dModels } from './useMapLayers';

type ToggleLayerFn = (layer: string, enabled: boolean) => void;
type ApplyBuildingLayersFn = (buildings: boolean, buildings3D: boolean) => void;

  /** Keys managed outside LAYER_IDS / toggleLayer (terrain uses setTerrain). */
const SKIP_SYNC_KEYS = new Set<keyof LayerSettings>([
  'terrain',
  'buildings',
  'buildings3D',
  'floodExposureYears',
  'observedWeatherTempPeriods',
  'observedWeatherWindPeriods',
  'observedWeatherWindDirectionPeriods',
  'observedWeatherPrecipPeriods',
  'observedWeatherTemp',
  'observedWeatherWind',
  'observedWeatherWindDirection',
  'observedWeatherPrecip',
  'forecastWeatherTempPeriods',
  'forecastWeatherPrecipPeriods',
  'forecastWeatherWindPeriods',
  'forecastWeatherTemp',
  'forecastWeatherPrecip',
  'forecastWeatherWind',
]);

/**
 * Apply LayerSettings visibility whenever the map loads, the style reloads,
 * or the user changes a toggle — keeps UI state and rendered layers in sync.
 */
export function useLayerSettingsSync(
  mapRef: React.MutableRefObject<MapboxMap | null>,
  layerSettings: LayerSettings,
  toggleLayer: ToggleLayerFn,
  applyBuildingLayers: ApplyBuildingLayersFn,
) {
  const { isMapLoaded } = useMapStore();

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const apply = () => {
      if (!isMapStyleReady(map)) return;
      prepareMapForLayerToggles(map);
      (Object.entries(layerSettings) as [keyof LayerSettings, LayerSettings[keyof LayerSettings]][]).forEach(
        ([key, enabled]) => {
          if (SKIP_SYNC_KEYS.has(key)) return;
          if (typeof enabled !== 'boolean') return;
          toggleLayer(key, enabled);
        },
      );
      applyBuildingLayers(layerSettings.buildings, layerSettings.buildings3D);
      enableMapbox3dModels(map);
    };

    apply();
    map.on('load', apply);
    map.on('style.load', apply);
    // One idle pass after tiles settle — promotion can miss layers that register late.
    map.once('idle', apply);
    return () => {
      map.off('load', apply);
      map.off('style.load', apply);
    };
  }, [mapRef, isMapLoaded, layerSettings, toggleLayer, applyBuildingLayers]);
}
